import { Link2Icon, PlayIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import useBrains from '@/hooks/useBrains';
import { AIAgentsSelect } from '@/modules/tryIt/components/ModalTryIt/AIAgentsSelect';

import { ChatActionsMenu } from './ChatActionsMenu';
import { ResetConversationButton } from './ResetConversationButton';
import { TranslateDropdown } from './TranslateDropdown';
import { useSessionContent } from '../../hooks/useSessionContent';
import { resetConversation } from '../../redux/actions';
import {
  selectBrainId,
  selectHasMessages,
  selectIsReplay,
  selectSessionStart,
  selectTryItSessionId,
} from '../../redux/selectors';

type Props = { collectionLanguage?: string; onClose?: () => void };

export const ChatHeader = ({ collectionLanguage, onClose }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const isReplay = useSelector(selectIsReplay);
  const brainId = useSelector(selectBrainId);
  const sessionId = useSelector(selectTryItSessionId);
  const sessionStart = useSelector(selectSessionStart);
  // `hasMessages` is read from the new slice's `selectHasMessages` — the
  // source of truth now that the new `<Messages>` renders directly from it.
  const hasMessages = useSelector(selectHasMessages);

  const { brain } = useBrains(brainId);
  const { data: content } = useSessionContent(
    isReplay ? sessionId : undefined,
    isReplay ? sessionStart : undefined
  );

  const title = isReplay ? (content?.user_name ?? t('common.anonymous')) : brain?.name;
  // Category routes keep the in-modal agent switcher (parity with the legacy modal).
  const isInCategoryContext = location.pathname.includes('/category/');

  return (
    <header className="flex h-14 shrink-0 items-center justify-between bg-background px-4 py-3">
      {isInCategoryContext ? (
        <AIAgentsSelect
          handleReset={() => dispatch(resetConversation())}
          handleCloseModal={() => onClose?.()}
          collectionLanguage={collectionLanguage}
        />
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-1.5 text-sm leading-5 font-semibold text-foreground">
          {!isReplay && (
            <>
              <PlayIcon size={16} className="shrink-0" />
              <span className="shrink-0">{t('try_it.testing')}</span>
              <span className="shrink-0">·</span>
            </>
          )}
          <span className="truncate">{title}</span>
        </div>
      )}
      <div className="flex shrink-0 items-center gap-2">
        {isReplay
          ? sessionId && (
              <>
                <CopyButton
                  data={window.location.href}
                  tooltip={t('common.copy_link')}
                  icon={<Link2Icon size={16} />}
                />
                <TranslateDropdown />
                <ChatActionsMenu sessionId={sessionId} />
              </>
            )
          : sessionId &&
            hasMessages && (
              <>
                <ResetConversationButton />
                <ChatActionsMenu sessionId={sessionId} />
              </>
            )}
      </div>
    </header>
  );
};
